import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import ProductCard from '../components/ui/ProductCard';
import InstagramFeed from '../components/ui/InstagramFeed';
import { fetchProducts } from '../api/products';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const products = await fetchProducts();
        const featured = products.filter(product => product.isFeatured && product.inStock);
        setFeaturedProducts(featured);
        setLoading(false);
      } catch (err) {
        console.error('Error loading featured products:', err);
        setError('Failed to load products. Please try again later.');
        setLoading(false);
      }
    };

    loadProducts();
  }, []);
  // Loading and error handling
  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-light">Loading featured products...</p>
        </div>
      </Layout>
    );
  }
  
  if (error) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="font-serif text-3xl font-bold text-primary mb-6">Error</h1>
          <p className="text-light mb-8">{error}</p>
          <Button onClick={() => window.location.reload()} variant="primary">
            Try Again
          </Button>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      {/* Hero Section with Shop CTA - Full-width image with minimal text */}
      <section className="relative h-screen">
        <div className="absolute inset-0 bg-black">
          <img 
            src="/images/home-1.jpg" 
            alt="Kala Hats Collection" 
            className="w-full h-full object-cover object-center opacity-70"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-light mb-6 text-shadow">
              Outlaw Souls
            </h1>
            <p className="text-xl md:text-2xl text-light mb-4 max-w-3xl mx-auto text-shadow">
              Born in Bali—where incense smoke dances with engine oil and sunsets feel like sermons
            </p>
            <p className="text-lg md:text-xl text-light mb-8 max-w-2xl mx-auto text-shadow opacity-90">
              A flag for those who live by their own rules
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button to="/products" size="lg">
                Join The Resistance
              </Button>
              <Button to="/about" variant="outline" size="lg">
                Our Rebellion
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-16 md:py-24 bg-black relative">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <img 
            src="/images/home-2.jpg" 
            alt="" 
            className="w-full h-full object-cover object-center"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
              Wearable Artifacts
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto mb-8"></div>
            <p className="text-light text-lg max-w-3xl mx-auto mb-4">
              <span className="block">Every hat carries a message.</span>
              <span className="block">Every boot leaves a trail.</span>
              <span className="block">Every shirt tells a story.</span>
            </p>
            <p className="text-light opacity-80 max-w-2xl mx-auto mb-8">
              Limited, meaningful, and unapologetically bold—crafted for modern outlaws, urban mystics, lone riders, and seekers of untamed beauty.
            </p>
            <p className="text-primary/90 text-sm max-w-2xl mx-auto mb-8">
              Active catalog in IDR with checkout flow powered by Xendit.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button to="/products" variant="outline">
              View All Products
            </Button>
          </div>
        </div>
      </section>

      {/* Custom Orders Section - Minimal with focus on CTA */}
      <section className="py-16 md:py-24 bg-secondary relative">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <img 
            src="/images/home-3.jpg" 
            alt="" 
            className="w-full h-full object-cover object-center"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-6">
            Create Your Ritual of Identity
          </h2>
          <div className="w-24 h-1 bg-primary mx-auto mb-8"></div>
          <p className="text-light text-lg max-w-3xl mx-auto mb-4">
            In a world obsessed with conformity, your style becomes your rebellion.
          </p>
          <p className="text-light opacity-80 max-w-2xl mx-auto mb-8">
            Work directly with our shadow culture artisans to manifest your unique vision into wearable art.
          </p>
          <Button to="/custom" variant="primary" size="lg">
            Begin Your Transformation
          </Button>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-black border-y border-gray-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
              Kala Care Club (Recurring)
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto mb-6"></div>
            <p className="text-light max-w-3xl mx-auto">
              For customers who want long-term hat care, monthly membership includes maintenance and priority service with recurring billing authorization.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="border border-gray-800 bg-secondary p-6 text-center">
              <h3 className="font-serif text-xl text-primary mb-2">Basic Care</h3>
              <p className="text-light text-2xl font-bold mb-3">Rp 249.000 / month</p>
              <p className="text-gray-400 text-sm">Monthly cleaning and shape maintenance for 1 hat.</p>
            </div>
            <div className="border border-primary bg-primary/10 p-6 text-center">
              <h3 className="font-serif text-xl text-primary mb-2">Artisan Care</h3>
              <p className="text-light text-2xl font-bold mb-3">Rp 499.000 / month</p>
              <p className="text-gray-400 text-sm">Care for up to 2 hats, priority queue, minor repair coverage.</p>
            </div>
            <div className="border border-gray-800 bg-secondary p-6 text-center">
              <h3 className="font-serif text-xl text-primary mb-2">Collector Care</h3>
              <p className="text-light text-2xl font-bold mb-3">Rp 899.000 / month</p>
              <p className="text-gray-400 text-sm">Care for up to 4 hats, express turnaround, seasonal restoration.</p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-400 text-sm mb-5">
              Recurring charges are only created with explicit customer consent and can be cancelled before the next billing cycle.
            </p>
            <Button to="/faq" variant="outline">
              View Membership Details
            </Button>
          </div>
        </div>
      </section>

      {/* Artisan Story Section - Full-width image with text overlay */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-black">
          <img 
            src="/images/home-4.jpg" 
            alt="Balinese Artisan" 
            className="w-full h-full object-cover object-center opacity-50"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-6">
              SASTRAJIWA: Artist. Musician. Storyteller.
            </h2>
            <p className="text-light text-lg mb-6">
              Born where ancient traditions collide with modern rebellion, SASTRAJIWA channels shadow culture into wearable revolution. Each piece emerges from a Bali workshop that breathes with the spirit of resistance.
            </p>
            <p className="text-light text-lg mb-6">
              For SASTRAJIWA, Kala is not just a brand—it's resistance. A living artwork. A ritual of identity in a world that demands conformity.
            </p>
            <p className="text-light opacity-90 text-lg mb-8">
              "We don't just make clothing. We forge armor for the soul, crafted for those who refuse to fade into the background."
            </p>
            <div className="flex flex-wrap gap-4">
              <Button to="/about" variant="primary">
                Meet The Rebel
              </Button>
              <Button to="/showroom" variant="outline">
                Visit The Workshop
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Feed Section - Improved with embed-like appearance */}
      <section className="py-16 md:py-24 bg-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-primary mb-4">
              Shadow Culture Chronicles
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto mb-8"></div>
            <p className="text-gray-700 text-lg max-w-3xl mx-auto mb-8">
              Witness the rebellion unfold. See how wearable artifacts take shape in our Bali sanctuary, where tradition dances with defiance.
              <span className="block mt-2 font-medium">@kala.hats</span>
            </p>
          </div>
          
          {/* Instagram Feed Component */}
          <InstagramFeed username="kala.hats" count={4} />
          
          <div className="text-center mt-12">
            <a 
              href="https://www.instagram.com/kala.hats/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 border border-primary text-primary hover:bg-primary hover:text-white transition-colors duration-300 rounded-md font-medium"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              View More on Instagram
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
